import "components/inputs/Select/Select.scss";

import { useState } from "react";
import CreatableSelect from "react-select/creatable";

// Deliberately permissive (just "something@something.something") since this
// only needs to catch obvious typos before hitting the backend, which does
// the real validation.
const EMAIL_PATTERN = /^\S+@\S+\.\S+$/;

type EmailOption = { label: string; value: string };

const toOption = (email: string): EmailOption => ({
  label: email,
  value: email,
});

// No dropdown menu, since there's no fixed list of options -- every value is
// typed in freehand and turned into a tag on Enter, Tab, or comma. Mirrors
// react-select's own recipe for a tags-style creatable input.
const HIDDEN_DROPDOWN_COMPONENTS = { DropdownIndicator: () => null };

type EmailAddressMultiSelectProps = {
  name: string;
  label?: string;
  value: string[];
  onChange: (emails: string[]) => void;
};

const EmailAddressMultiSelect = ({
  name,
  label,
  value,
  onChange,
}: EmailAddressMultiSelectProps) => {
  const [inputValue, setInputValue] = useState("");

  const commitInputValue = () => {
    const trimmed = inputValue.trim().replace(/,+$/, "");
    if (trimmed === "") return;
    if (!value.some((email) => email.toLowerCase() === trimmed.toLowerCase())) {
      onChange([...value, trimmed]);
    }
    setInputValue("");
  };

  return (
    <div className="Select">
      {label && <label>{label}</label>}
      <div className="select select--full">
        <CreatableSelect
          inputId={name}
          classNamePrefix="kaboodle"
          components={HIDDEN_DROPDOWN_COMPONENTS}
          isMulti
          isClearable={false}
          menuIsOpen={false}
          placeholder="Type an email and press Enter..."
          inputValue={inputValue}
          value={value.map(toOption)}
          onChange={(options) =>
            onChange((options ?? []).map((option) => option.value))
          }
          onInputChange={(newValue, actionMeta) => {
            if (actionMeta.action === "input-change") {
              setInputValue(newValue);
            }
          }}
          onKeyDown={(event) => {
            if (!inputValue) return;
            if (
              event.key === "Enter" ||
              event.key === "Tab" ||
              event.key === ","
            ) {
              event.preventDefault();
              commitInputValue();
            }
          }}
          onBlur={commitInputValue}
        />
      </div>
      {value.some((email) => !EMAIL_PATTERN.test(email)) && (
        <div className="error-message">
          One or more entries isn&apos;t a valid email address.
        </div>
      )}
    </div>
  );
};

export default EmailAddressMultiSelect;
